import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Activity, Flame, TrendingUp, Target, Tv, RefreshCw, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { countryToFlag } from "../lib/countryFlag";
import { formatLiveMinute } from "../lib/liveMinute";
import TopNav from "../components/TopNav";
import DominancePanel from "../components/DominancePanel";
import GoalCelebration from "../components/GoalCelebration";
import { readGoalPrefsLocal } from "../components/GoalAlertSettings";
import { HlsVideoPlayer } from "../components/HlsVideoPlayer";
import WhereToWatchSection from "../components/WhereToWatchSection";
import PenaltyShootout from "../components/PenaltyShootout";

// Hosts known to enforce a referrer/parent-origin check at runtime.
// For these we route the iframe through our backend's referrer-spoofing
// proxy at /api/streams/embed?u=<encoded>&page=<aiscore-page>, which
// fetches the upstream HTML server-side with `Referer: aiscore.com`
// and injects a JS shim that overrides document.referrer before the
// upstream bundle loads.
const REFERRER_PROTECTED_HOSTS = [
  "widgets.thesports01.com",
  "widgets.sports01.com",
  "tv.thesports01.com",
];

const buildIframeSrc = (stream) => {
  if (!stream?.url) return "";
  try {
    const host = new URL(stream.url).hostname;
    if (REFERRER_PROTECTED_HOSTS.includes(host)) {
      const params = new URLSearchParams({ u: stream.url });
      if (stream.source_url) params.set("page", stream.source_url);
      return `${API}/streams/embed?${params.toString()}`;
    }
  } catch {
    /* fall through to direct URL */
  }
  return stream.url;
};

const StatusPill = ({ status, minute, minuteExtra }) => {
  const isLive = ["1H", "2H", "ET", "P", "LIVE"].includes(status);
  const isHT = status === "HT";
  const label = isLive
    ? formatLiveMinute(minute, minuteExtra, status) || "'"
    : isHT
    ? "HT"
    : status;
  const inStoppage =
    isLive && typeof minuteExtra === "number" && minuteExtra > 0;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-bold uppercase tracking-wider ${
        inStoppage
          ? "bg-amber-500/20 text-amber-200 border border-amber-500/50"
          : isLive
          ? "bg-destructive/15 text-destructive border border-destructive/40"
          : isHT
          ? "bg-warning/15 text-warning border border-warning/40"
          : "bg-muted text-muted-foreground border border-border"
      }`}
      title={inStoppage ? `Stoppage time — ${minuteExtra} added minute${minuteExtra === 1 ? "" : "s"}` : undefined}
    >
      {isLive && <span className={`w-1.5 h-1.5 rounded-full ${inStoppage ? "bg-amber-300" : "bg-destructive"} animate-pulse`} />}
      {label}
    </span>
  );
};

const LMIBar = ({ value, label }) => {
  const pct = Math.round((value || 0) * 100);
  return (
    <div className="flex flex-col gap-1.5">
      <div className="flex justify-between items-center text-xs">
        <span className="text-muted-foreground font-semibold">{label}</span>
        <span className="font-tabular font-bold text-white">{pct}</span>
      </div>
      <div className="h-2.5 rounded-full bg-muted overflow-hidden">
        <div
          className={`h-full rounded-full transition-all duration-500 ${
            pct >= 60 ? "bg-success" : pct >= 40 ? "bg-warning" : "bg-muted-foreground/60"
          }`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
};

const getRiskColor = (r) => (r === "Low" ? "risk-low" : r === "High" ? "risk-high" : "risk-medium");
const getConfidenceColor = (c) => (c >= 75 ? "#22c55e" : c >= 60 ? "#fbbf24" : "#ef4444");

/**
 * Given a market pick and current live state, return {label, tone} telling the
 * user whether the pre-match pick is still on track.
 */
const computeLiveEdge = (market, live, match) => {
  if (!live || !market) return null;
  const minute = live.minute ?? null;
  const home = live.score?.home ?? match?.goals_home ?? null;
  const away = live.score?.away ?? match?.goals_away ?? null;
  if (home == null || away == null || minute == null) return null;
  const t = market.market_type || "";
  const pred = (market.prediction || "").toLowerCase();
  const totalGoals = home + away;
  const lateGame = minute >= 70;

  // Iter 145s — Detect HT-specific markets (HT BTTS, HT O/U, HT Result).
  // Until I added this branch the live edge was using the running
  // full-match score even for HT picks — so a 1st-half-only condition
  // could flip from "Pending" to "Lost" purely because of a 2nd-half
  // goal that has nothing to do with the HT outcome.
  const isHtMarket =
    t.startsWith("HT ") ||
    t.toLowerCase().includes("halftime") ||
    t.toLowerCase().includes("half time") ||
    t.toLowerCase().includes("1st half") ||
    t.toLowerCase().includes("first half");

  if (isHtMarket) {
    const status = (live.status || match?.status || "").toUpperCase();
    // Running 1st-half tally is just the live score during 1H.  Once 1H
    // ends, API-Sports populates `score.ht_home / ht_away` and we freeze
    // on those — even when the 2nd-half score later moves.
    const inFirstHalf = status === "1H";
    const ht_home = inFirstHalf ? home : (live.score?.ht_home ?? null);
    const ht_away = inFirstHalf ? away : (live.score?.ht_away ?? null);

    // No HT data yet (e.g. we're 1H and frame just loaded with FT 0-0
    // before the score block parsed).  Stay neutral.
    if (ht_home == null || ht_away == null) {
      return { label: "Pending", tone: "neutral" };
    }
    const htTotal = ht_home + ht_away;
    const htHomeAhead = ht_home > ht_away;
    const htAwayAhead = ht_away > ht_home;
    const htDraw = ht_home === ht_away;
    const htBoth = ht_home >= 1 && ht_away >= 1;
    // Settled = first half is over (status no longer "1H").
    const htSettled = !inFirstHalf;

    // HT BTTS
    if (t.toUpperCase().includes("BTTS") || t.toLowerCase().includes("both teams")) {
      if (pred.startsWith("yes")) {
        if (htBoth) return { label: "Hit ✓", tone: "good" };
        if (htSettled) return { label: "Lost", tone: "bad" };
        // Still in 1H — fade only if late in the half and only one side scored.
        if (minute >= 40 && !htBoth) return { label: "Fading", tone: "warn" };
        return { label: "Pending", tone: "neutral" };
      }
      if (pred.startsWith("no")) {
        if (htBoth) return { label: "Lost", tone: "bad" };
        if (htSettled) return { label: "Hit ✓", tone: "good" };
        return { label: "Tracking", tone: "good" };
      }
    }

    // HT Over/Under
    if (t.toLowerCase().includes("o/u") || t.toLowerCase().includes("over") || pred.includes("over") || pred.includes("under")) {
      const lineMatch = (t + " " + pred).match(/(\d+(?:\.\d+)?)/);
      const line = lineMatch ? parseFloat(lineMatch[1]) : 0.5;
      if (pred.startsWith("over")) {
        if (htTotal > line) return { label: "Hit ✓", tone: "good" };
        if (htSettled) return { label: "Lost", tone: "bad" };
        return { label: "Pending", tone: "neutral" };
      }
      if (pred.startsWith("under")) {
        if (htTotal > line) return { label: "Lost", tone: "bad" };
        if (htSettled && htTotal <= line) return { label: "Hit ✓", tone: "good" };
        return { label: "Tracking", tone: "good" };
      }
    }

    // HT 1X2 / Result
    if (t.toLowerCase().includes("1x2") || t.toLowerCase().includes("result") || pred === "1" || pred === "2" || pred === "x") {
      const wantsHome = pred.includes("home") || pred === "1";
      const wantsAway = pred.includes("away") || pred === "2";
      const wantsDraw = pred.includes("draw") || pred === "x";
      if (wantsHome) {
        if (htHomeAhead) return htSettled ? { label: "Hit ✓", tone: "good" } : { label: "Tracking", tone: "good" };
        if (htSettled) return { label: "Lost", tone: "bad" };
        return { label: htDraw ? "Level" : "Fading", tone: htDraw ? "neutral" : "warn" };
      }
      if (wantsAway) {
        if (htAwayAhead) return htSettled ? { label: "Hit ✓", tone: "good" } : { label: "Tracking", tone: "good" };
        if (htSettled) return { label: "Lost", tone: "bad" };
        return { label: htDraw ? "Level" : "Fading", tone: htDraw ? "neutral" : "warn" };
      }
      if (wantsDraw) {
        if (htDraw) return htSettled ? { label: "Hit ✓", tone: "good" } : { label: "Tracking", tone: "good" };
        if (htSettled) return { label: "Lost", tone: "bad" };
        return { label: "Fading", tone: "warn" };
      }
    }

    return { label: "Pending", tone: "neutral" };
  }

  // ── Full-match markets below ─────────────────────────────────────────

  // 1X2 / Match Result
  if (t.includes("1X2") || t.includes("Match Result")) {
    if (pred.includes("home")) {
      if (home > away) return { label: "Tracking", tone: "good" };
      if (home < away) return { label: "Fading", tone: "bad" };
      return lateGame ? { label: "Must score", tone: "warn" } : { label: "Level", tone: "neutral" };
    }
    if (pred.includes("away")) {
      if (away > home) return { label: "Tracking", tone: "good" };
      if (away < home) return { label: "Fading", tone: "bad" };
      return lateGame ? { label: "Must score", tone: "warn" } : { label: "Level", tone: "neutral" };
    }
    if (pred.includes("draw")) {
      if (home === away) return { label: "Tracking", tone: "good" };
      return lateGame ? { label: "Fading", tone: "bad" } : { label: "Level", tone: "neutral" };
    }
  }

  // BTTS (full match)
  if (t.includes("BTTS")) {
    const bothScored = home >= 1 && away >= 1;
    if (pred.startsWith("yes")) {
      if (bothScored) return { label: "Hit ✓", tone: "good" };
      if (lateGame) return { label: "Fading", tone: "bad" };
      return { label: "Pending", tone: "neutral" };
    }
    if (pred.startsWith("no")) {
      if (!bothScored && lateGame) return { label: "Tracking", tone: "good" };
      if (bothScored) return { label: "Lost", tone: "bad" };
      return { label: "Pending", tone: "neutral" };
    }
  }

  // Over/Under (full match)
  if (t.includes("Over/Under") || t.includes("Goals Total")) {
    const lineMatch = pred.match(/(\d+(?:\.\d+)?)/);
    const line = lineMatch ? parseFloat(lineMatch[1]) : 2.5;
    if (pred.startsWith("over")) {
      if (totalGoals > line) return { label: "Hit ✓", tone: "good" };
      if (lateGame && totalGoals <= line) return { label: "Fading", tone: "bad" };
      return { label: "Pending", tone: "neutral" };
    }
    if (pred.startsWith("under")) {
      if (totalGoals > line) return { label: "Lost", tone: "bad" };
      if (lateGame && totalGoals <= line) return { label: "Tracking", tone: "good" };
      return { label: "Pending", tone: "neutral" };
    }
  }

  // Double Chance
  if (t.includes("Double Chance")) {
    const covers = {
      "home win or draw": home >= away,
      "away win or draw": away >= home,
      "home win or away win": home !== away,
    };
    const ok = covers[pred];
    if (ok === true) return { label: "Tracking", tone: "good" };
    if (ok === false) return { label: "Fading", tone: "bad" };
  }

  return null;
};

const LiveEdgeChip = ({ edge }) => {
  if (!edge) return null;
  const tones = {
    good: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30",
    bad: "bg-rose-500/15 text-rose-300 border-rose-500/30",
    warn: "bg-amber-500/15 text-amber-200 border-amber-500/30",
    neutral: "bg-slate-700/40 text-foreground border-border/40",
  };
  return (
    <span
      className={`text-[10px] font-tabular uppercase tracking-wider px-1.5 py-0.5 rounded border ${tones[edge.tone] || tones.neutral}`}
      data-testid="live-edge-chip"
    >
      {edge.label}
    </span>
  );
};

const DISCLAIMER_KEY = "ndakubet-stream-disclaimer-ack";

const VIEW_PREF_KEY = "ndakubet-stream-view-pref"; // shared with LiveStreamModal

const LiveStreamPlayer = ({ fixtureId, homeTeam, awayTeam }) => {
  const [stream, setStream] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [acknowledged, setAcknowledged] = useState(() => {
    try { return localStorage.getItem(DISCLAIMER_KEY) === "1"; } catch { return false; }
  });
  const [hidden, setHidden] = useState(false);
  const [feed, setFeed] = useState(() => {
    try { return localStorage.getItem(VIEW_PREF_KEY) || "3d"; } catch { return "3d"; }
  });
  const [liveVideoFailed, setLiveVideoFailed] = useState(false);

  useEffect(() => {
    if (!fixtureId) return;
    let cancelled = false;
    setLoading(true);
    setLiveVideoFailed(false);
    axios.get(`${API}/streams/${fixtureId}`)
      .then((r) => {
        if (cancelled) return;
        setStream(r.data);
        setLoading(false);
      })
      .catch(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [fixtureId]);

  // Fall back to 3D when the loaded stream has no live video.
  useEffect(() => {
    if (stream && !stream.live_video && feed === "live") setFeed("3d");
  }, [stream, feed]);

  useEffect(() => {
    try { localStorage.setItem(VIEW_PREF_KEY, feed); } catch {}
  }, [feed]);

  const onLiveVideoError = () => {
    if (liveVideoFailed) return;
    setLiveVideoFailed(true);
    setFeed("3d");
    toast.info("Live broadcast dropped — switched to 3D match animation");
  };

  const refresh = async () => {
    setRefreshing(true);
    try {
      await axios.post(`${API}/streams/${fixtureId}/refresh`);
      const r = await axios.get(`${API}/streams/${fixtureId}`);
      setStream(r.data);
      if (r.data?.available) toast.success("Fresh stream loaded");
      else toast.info("No public stream yet");
    } catch { toast.error("Couldn't reach stream provider"); }
    finally { setRefreshing(false); }
  };

  const accept = () => {
    try { localStorage.setItem(DISCLAIMER_KEY, "1"); } catch {}
    setAcknowledged(true);
  };

  if (hidden) return null;

  if (!acknowledged) {
    return (
      <Card className="border-border nd-fade-in" data-testid="stream-disclaimer-card">
        <CardContent className="p-6 flex flex-col sm:flex-row items-start gap-4">
          <AlertTriangle className="w-5 h-5 text-warning shrink-0 mt-0.5" />
          <div className="flex-1 text-sm">
            <div className="font-semibold mb-1">Third-party stream notice</div>
            <p className="text-muted-foreground">
              Live streams are embedded from public third-party providers.
              NDAKUBET does not host any broadcast content. Check your local
              streaming laws. Ads inside the player are not from NDAKUBET.
            </p>
          </div>
          <div className="flex gap-2 shrink-0">
            <Button
              variant="outline"
              onClick={() => setHidden(true)}
              className="h-9 rounded-full px-4 border-border bg-card hover:bg-muted text-foreground"
              data-testid="stream-disclaimer-hide"
            >
              Skip
            </Button>
            <Button
              onClick={accept}
              className="h-9 rounded-full px-4 bg-primary text-primary-foreground hover:bg-primary/90 font-semibold"
              data-testid="stream-disclaimer-accept-inline"
            >
              I understand
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (loading) {
    return (
      <Card className="border-border" data-testid="stream-loading">
        <CardContent className="p-6 text-center text-muted-foreground">
          <Activity className="w-5 h-5 animate-pulse mx-auto mb-2" />
          Searching for a live stream…
        </CardContent>
      </Card>
    );
  }

  if (!stream?.available) {
    return (
      <Card className="border-border" data-testid="stream-none">
        <CardContent className="p-6 flex flex-wrap items-center gap-3 justify-between">
          <div className="flex items-center gap-2 text-sm">
            <Tv className="w-4 h-4 text-muted-foreground" />
            <span>No public stream found for this match — follow the LMI stats below.</span>
          </div>
          <Button
            onClick={refresh}
            disabled={refreshing}
            variant="outline"
            size="sm"
            className="h-8 rounded-full px-3 border-border bg-card hover:bg-muted text-foreground"
            data-testid="stream-retry-btn"
          >
            <RefreshCw className={`w-3 h-3 mr-1 ${refreshing ? "animate-spin" : ""}`} />
            Retry
          </Button>
        </CardContent>
      </Card>
    );
  }

  const hasLive = !!stream?.live_video?.url && !liveVideoFailed;
  const hasAnim = !!stream?.animation_3d?.url;

  return (
    <Card className="border-border overflow-hidden" data-testid="stream-player-card">
      <div className="flex items-center gap-1 p-2 border-b border-border bg-card/40">
        <button
          type="button"
          onClick={() => hasLive && setFeed("live")}
          disabled={!hasLive}
          data-testid="livepage-stream-tab-live"
          title={hasLive ? "Live broadcast" : "No live broadcast available"}
          className={`inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full text-xs font-semibold transition-colors ${
            feed === "live" && hasLive
              ? "bg-foreground text-background"
              : hasLive
              ? "text-muted-foreground hover:text-foreground bg-muted"
              : "text-muted-foreground/40 cursor-not-allowed bg-muted/40"
          }`}
        >
          Watch Live
          {!hasLive && <span className="text-[9px] opacity-60 ml-1">·N/A</span>}
        </button>
        <button
          type="button"
          onClick={() => hasAnim && setFeed("3d")}
          disabled={!hasAnim}
          data-testid="livepage-stream-tab-3d"
          className={`inline-flex items-center gap-1.5 h-8 px-3.5 rounded-full text-xs font-semibold transition-colors ${
            feed === "3d"
              ? "bg-foreground text-background"
              : hasAnim
              ? "text-muted-foreground hover:text-foreground bg-muted"
              : "text-muted-foreground/40 cursor-not-allowed bg-muted/40"
          }`}
        >
          3D Match Animation
        </button>
      </div>
      <div className="aspect-video bg-black relative group">
        {feed === "live" && hasLive ? (
          stream.live_video.kind === "hls" ? (
            <HlsVideoPlayer
              src={stream.live_video.url}
              onError={onLiveVideoError}
              className="absolute inset-0 w-full h-full"
            />
          ) : (
            <iframe
              src={stream.live_video.url}
              title={`${homeTeam} vs ${awayTeam} — live`}
              allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
              allowFullScreen
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-presentation"
              className="absolute inset-0 w-full h-full border-0"
              data-testid="stream-iframe-live-page"
            />
          )
        ) : hasAnim ? (
          <iframe
            src={buildIframeSrc(stream)}
            title={`${homeTeam} vs ${awayTeam} — 3D animation`}
            allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
            allowFullScreen
            referrerPolicy="origin"
            sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-presentation"
            className="absolute inset-0 w-full h-full border-0"
            data-testid="stream-iframe-inline"
          />
        ) : null}
        {/* Always-visible CTA in the corner so users have a guaranteed
            path to the upstream page even if the upstream's runtime
            anti-frame check empties the iframe. The proxy fixes the
            "domain unauthorized" message but some upstream apps still
            black-screen due to server-side origin validation; this
            ensures the user is never stranded. */}
        {stream.source_url && (
          <a
            href={stream.source_url}
            target="_blank"
            rel="noreferrer"
            data-testid="stream-open-source"
            className="absolute top-3 right-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-black/70 backdrop-blur-sm border border-white/20 text-white text-xs font-semibold hover:bg-black/90 transition-colors opacity-80 hover:opacity-100"
          >
            <Tv className="w-3.5 h-3.5" />
            Open on aiscore
          </a>
        )}
      </div>
      <CardContent className="flex flex-wrap items-center justify-between gap-2 p-3">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Tv className="w-3.5 h-3.5" />
          <span>source: {stream.source}</span>
          {stream.source_url && (
            <a href={stream.source_url} target="_blank" rel="noreferrer" className="underline underline-offset-2 hover:text-foreground">
              original page
            </a>
          )}
        </div>
        <Button
          onClick={refresh}
          disabled={refreshing}
          variant="outline"
          size="sm"
          className="h-8 rounded-full px-3 border-border bg-card hover:bg-muted text-foreground"
          data-testid="stream-refresh-inline-btn"
        >
          <RefreshCw className={`w-3 h-3 mr-1 ${refreshing ? "animate-spin" : ""}`} />
          Refresh
        </Button>
      </CardContent>
    </Card>
  );
};

const LiveMatchPage = () => {
  const { fixtureId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [goalEvent, setGoalEvent] = useState(null); // { key, side, scorer }
  const prevScoreRef = useRef({ home: null, away: null });
  // Iter 145q — Track whether we've EVER had a successful load so the
  // 1-Hz poller doesn't spam "Failed to load match" toasts on transient
  // network blips (which were stacking up because multiple in-flight
  // requests all saw `loading=true` before the first one resolved).
  const everLoadedRef = useRef(false);
  const errorToastShownRef = useRef(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/live/match/${fixtureId}`, { timeout: 30000 });
      setData(r.data);
      everLoadedRef.current = true;
      errorToastShownRef.current = false;

      // Goal detection: compare current score to last-seen score.
      const live = r.data?.live || {};
      const score = live.score || {
        home: r.data?.match?.goals_home,
        away: r.data?.match?.goals_away,
      };
      const prev = prevScoreRef.current;
      const homeVal = Number(score?.home ?? 0);
      const awayVal = Number(score?.away ?? 0);
      if (prev.home != null && homeVal > prev.home) {
        setGoalEvent({
          key: `home-${Date.now()}`,
          side: "home",
          scorer: r.data?.match?.home_team?.name || "Home",
        });
      } else if (prev.away != null && awayVal > prev.away) {
        setGoalEvent({
          key: `away-${Date.now()}`,
          side: "away",
          scorer: r.data?.match?.away_team?.name || "Away",
        });
      }
      prevScoreRef.current = { home: homeVal, away: awayVal };
    } catch (e) {
      // Iter 145q — Only surface a toast when we have NEVER successfully
      // loaded the match.  Once any poll succeeds, transient blips are
      // silently swallowed so the carousel doesn't litter the screen
      // with stale error banners while the match data is still on-screen.
      if (!everLoadedRef.current && !errorToastShownRef.current) {
        console.error("Live match load failed:", e);
        toast.error("Failed to load match");
        errorToastShownRef.current = true;
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // Iter 145q — Skip the next tick when a request is still in flight
    // so we don't stack 30s timeouts during a slow upstream call.  This
    // is what was causing multi-toast spam on the live page.
    let inFlight = false;
    const tick = async () => {
      if (inFlight) return;
      if (document.visibilityState !== "visible") return;
      inFlight = true;
      try { await load(); } finally { inFlight = false; }
    };
    const handle = setInterval(tick, 1000);
    return () => clearInterval(handle);
    // eslint-disable-next-line
  }, [fixtureId]);

  if (loading) {
    return (
      <div
        className="min-h-screen bg-background text-foreground flex items-center justify-center"
        
      >
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-foreground" />
      </div>
    );
  }

  if (!data?.match) {
    return (
      <div
        className="min-h-screen bg-background text-foreground flex items-center justify-center"
        
      >
        <div className="text-center text-foreground">
          <p>Match not found.</p>
          <Button onClick={() => navigate("/")} className="mt-4" data-testid="back-home-btn">
            Back to Home
          </Button>
        </div>
      </div>
    );
  }

  const { match, predictions, live, is_live } = data;
  const markets = predictions?.markets || predictions?.top_predictions || [];
  const score = live?.score || { home: match.goals_home, away: match.goals_away };
  const minute = live?.minute;
  const minuteExtra = live?.minute_extra;
  const status = live?.status || match.status;
  // Trust live payload: if we got a minute back, the match is in play regardless
  // of whatever the DB status field says (it's often stale for late-ingested fixtures).
  const trulyLive = Boolean(is_live || (minute != null && minute >= 0) ||
    ["1H", "2H", "HT", "ET", "P", "LIVE"].includes(status));

  return (
    <div className="min-h-screen bg-background text-foreground">
      <TopNav />

      <GoalCelebration
        trigger={(() => {
          const prefs = readGoalPrefsLocal();
          if (!prefs.goal_alerts_enabled) return null;
          return goalEvent?.key;
        })()}
        side={goalEvent?.side || "home"}
        scorer={goalEvent?.scorer}
        soundEnabled={(() => {
          const p = readGoalPrefsLocal();
          return p.goal_alerts_enabled && p.goal_sound_enabled;
        })()}
        onDone={() => setGoalEvent(null)}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 flex items-center justify-between">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="text-muted-foreground hover:text-foreground"
          data-testid="back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>
        {trulyLive && (
          <div className="flex items-center gap-2 text-success text-xs font-tabular uppercase tracking-wider" data-testid="live-refresh-badge">
            <Activity className="w-4 h-4 animate-pulse" />
            Live · updating every 1s
          </div>
        )}
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-8">
        {/* Live stream player — aisoccer or admin override, if any */}
        <LiveStreamPlayer
          fixtureId={fixtureId}
          homeTeam={match.home_team.name}
          awayTeam={match.away_team.name}
        />

        {/* Live header card */}
        <Card className="border-border nd-fade-in" data-testid="live-header-card">
          <CardContent className="p-8">
            <div className="flex items-center justify-between mb-4">
              <Badge variant="outline" className="border-border text-foreground">
                <span className="mr-1" aria-hidden>{countryToFlag(match.league?.country)}</span>
                {match.league.name} • {match.league.country}
              </Badge>
              <StatusPill status={status} minute={minute} minuteExtra={minuteExtra} />
            </div>

            <div className="grid grid-cols-3 items-center gap-6 mb-6">
              <div className="text-center">
                {match.home_team.logo && (
                  <img
                    src={match.home_team.logo}
                    alt={match.home_team.name}
                    className="w-20 h-20 object-contain mx-auto mb-2"
                  />
                )}
                <h2 className="text-xl font-bold text-white">{match.home_team.name}</h2>
              </div>

              <div className="text-center">
                {(score.home != null || score.away != null) ? (
                  <div
                    className="text-5xl font-tabular font-bold text-white tabular-nums"
                    data-testid="live-scoreboard"
                  >
                    {score.home ?? 0}
                    <span className="text-muted-foreground mx-3">·</span>
                    {score.away ?? 0}
                  </div>
                ) : (
                  <div className="text-3xl font-bold text-muted-foreground">VS</div>
                )}
                <p className="text-xs text-muted-foreground mt-1">
                  {new Date(match.date).toLocaleString()}
                </p>
              </div>

              <div className="text-center">
                {match.away_team.logo && (
                  <img
                    src={match.away_team.logo}
                    alt={match.away_team.name}
                    className="w-20 h-20 object-contain mx-auto mb-2"
                  />
                )}
                <h2 className="text-xl font-bold text-white">{match.away_team.name}</h2>
              </div>
            </div>

            {/* LMI bars (live or at HT) */}
            {live?.lmi && (
              <div
                className="grid grid-cols-2 gap-6 max-w-3xl mx-auto pt-6 border-t border-border"
                data-testid="live-lmi-section"
              >
                <LMIBar value={live.lmi.home} label={`LMI · ${match.home_team.name}`} />
                <LMIBar value={live.lmi.away} label={`LMI · ${match.away_team.name}`} />
              </div>
            )}

            {/* Iter 247 — Penalty shoot-out (status == "P" or after AET→PEN) */}
            {live?.penalty && (
              <PenaltyShootout
                penalty={live.penalty}
                homeTeam={match.home_team.name}
                awayTeam={match.away_team.name}
                homeFlag={countryToFlag(match.home_team.country)}
                awayFlag={countryToFlag(match.away_team.country)}
                status={status}
              />
            )}

            {/* In-play signals */}
            {live?.triggers?.length > 0 && (
              <div className="mt-5 space-y-2" data-testid="live-triggers">
                {live.triggers.map((t, i) => {
                  // Legacy: some older snapshots stored the trigger as
                  // a plain string.  New backend (halftime_lmi.py)
                  // emits a structured object: { trigger, condition,
                  // entry_odds, risk, confidence }.
                  if (typeof t === "string") {
                    return (
                      <div key={i} className="flex items-start gap-2 text-sm text-emerald-300">
                        <Flame className="w-4 h-4 mt-0.5 shrink-0" />
                        <span>{t}</span>
                      </div>
                    );
                  }
                  const conf = t?.confidence;
                  const riskTone =
                    t?.risk === "Low"    ? "border-emerald-500/40 text-emerald-200 bg-emerald-500/5" :
                    t?.risk === "High"   ? "border-rose-500/40 text-rose-200 bg-rose-500/5" :
                                           "border-amber-500/40 text-amber-200 bg-amber-500/5";
                  return (
                    <div
                      key={i}
                      className="flex items-start gap-2 text-sm rounded-lg border border-emerald-500/30 bg-emerald-500/[0.04] p-2.5"
                      data-testid={`live-trigger-${i}`}
                    >
                      <Flame className="w-4 h-4 mt-0.5 shrink-0 text-emerald-300" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold text-emerald-200">{t?.trigger || "Trigger"}</span>
                          {t?.risk && (
                            <span className={`text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full border ${riskTone}`}>
                              {t.risk}
                            </span>
                          )}
                          {Number.isFinite(conf) && (
                            <span className="text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-full bg-white/[0.04] border border-white/10 text-foreground">
                              {Math.round(conf)}%
                            </span>
                          )}
                        </div>
                        {t?.condition && (
                          <div className="text-[12px] text-muted-foreground mt-0.5">
                            {t.condition}
                          </div>
                        )}
                        {t?.entry_odds && (
                          <div className="text-[11px] text-muted-foreground/80 mt-0.5">
                            Entry odds <span className="font-tabular text-foreground">{t.entry_odds}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Dominance panel — shots on/off target + dangerous attacks */}
        {trulyLive && (
          <DominancePanel
            homeStats={live?.stats?.home}
            awayStats={live?.stats?.away}
            homeLmi={live?.lmi?.home}
            awayLmi={live?.lmi?.away}
            homeName={match.home_team.name}
            awayName={match.away_team.name}
          />
        )}

        {/* Iter 138 — Where to Watch on TV (live games included) */}
        <WhereToWatchSection
          matchInfo={{
            fixture_id: match.fixture_id || match.id,
            home_team: match.home_team?.name,
            away_team: match.away_team?.name,
            date: match.date,
            league: match.league?.name,
          }}
        />

        {/* Predictions or fallback note */}
        {markets.length > 0 ? (
          <div>
            <div className="flex items-center gap-2 mb-4">
              <TrendingUp className="w-5 h-5 text-success" />
              <h3 className="text-lg font-bold text-white">
                {trulyLive ? "Pre-match Picks (still relevant)" : "Predictions"}
              </h3>
              <Badge className="bg-muted text-foreground border border-border">
                {markets.length} markets
              </Badge>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="live-markets-grid">
              {markets.map((m, i) => {
                const edge = trulyLive ? computeLiveEdge(m, live, match) : null;
                return (
                <Card key={i} className="border-border" data-testid={`live-market-${i}`}>
                  <CardHeader className="pb-2">
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-base text-white">{m.market_type}</CardTitle>
                      <div className="flex items-center gap-2">
                        <LiveEdgeChip edge={edge} />
                        <Badge className={`${getRiskColor(m.risk_level)} text-xs`}>
                          {m.risk_level}
                        </Badge>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-base font-semibold text-white">{m.prediction}</span>
                      <span
                        className="text-lg font-bold"
                        style={{ color: getConfidenceColor(m.confidence) }}
                      >
                        {m.confidence}%
                      </span>
                    </div>
                    <div className="confidence-meter">
                      <div
                        className="confidence-fill"
                        style={{
                          width: `${m.confidence}%`,
                          background: getConfidenceColor(m.confidence),
                        }}
                      />
                    </div>
                  </CardContent>
                </Card>
                );
              })}
            </div>
          </div>
        ) : (
          <Card className="border-border" data-testid="live-no-markets-card">
            <CardContent className="p-6 text-center">
              <Target className="w-8 h-8 text-muted-foreground mx-auto mb-3" />
              <h3 className="text-base font-semibold text-foreground mb-1">
                {trulyLive ? "In-play model active" : "Building consensus"}
              </h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                {predictions?.note ||
                  (trulyLive
                    ? "Lean on the live momentum bars and score above for in-play decisions."
                    : "We're still gathering enough recent form data to publish a confident consensus for this fixture. Check back shortly.")}
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default LiveMatchPage;
